import { Button, CheckboxField, Divider, Flex, Image, Loader, Menu, MenuButton } from '@aws-amplify/ui-react';
import { forwardRef, useContext, useImperativeHandle, useMemo, useState } from 'react';
import { BiChevronDown } from 'react-icons/bi';
import { BsArrowsCollapseVertical, BsArrowsExpandVertical } from 'react-icons/bs';
import { FaRegEye, FaRegEyeSlash } from 'react-icons/fa';
import ReactModal from 'react-modal';
import { Link } from 'react-router-dom';
import { TableRowHeader } from '../custom-components/TableRows/TableRowHeader';
import { GlobalContext } from '../global-context';
import { AccountBucket } from '../types/people-search.types';
import { CLOUDFRONT_DOMAIN } from '../utils/Constants';
import { Entity, FieldConfig, SortOptions, UrlVersion } from '../utils/CustomTypes';
import General from '../utils/GeneralUtils';
import { isNewExportEnabled } from '../utils/newExportFeature';
import { TableFields } from '../utils/TableFields';
import './CustomList.scss';
import LoadingBars from './LoadingBars';
import NrMatchesSnippet from './NrMatchesSnippet';
import { TableRowContact, TableRowHeaderContact } from './TableRows/TableRowContact';
import { TableRowContactKeystone, TableRowHeaderContactKeystone } from './TableRows/TableRowContactKeystone';
import { isKeystoneVersion } from '../utils/Constants';
import { TableRowDomain } from './TableRows/TableRowDomain';
import { TableRowHeaderInstitution, TableRowInstitution } from './TableRows/TableRowInstitution';
import { TableRowHeaderRfp, TableRowRFP } from './TableRows/TableRowRFP';
import { PercentileIndicator } from './PercentileIndicator';
import UniversalTag from './UniversalTag';

export enum OnListEventType {
}

export type OnListEventProps = {
  type: OnListEventType,
  data: any
}

const CustomList = ({
  searchResponse,
  isLoading,
  isPaginationLoading = false,
  isExpanded,
  isExporting,
  isAllSortingDisabled = false,
  onSort,
  onExport,
  onNewExport,
  onAddItemsToBeacon,
  onExpandToggle,
  onEvent,
  showCheckbox = true,
  showExpandButton = true,
  showExportButton = true,
  showAddToBeaconButton = false,
  showRowActionButtons = true,
  showSubheader = true,
  showMainInstitution = true,
  showColumnVisibilityButton = true,
  stickyHeader = false,
  currentSort,
  tableFields,
  entity,
  selectedRows: externalSelectedRows,
  onSelectionChange,
  groupedData,
  groupedTotalContacts,
  groupedTotalAccounts,
  onLoadMoreContacts,
  loadingMoreAccountIds,
}: {
  searchResponse: any,
  isLoading: boolean,
  isPaginationLoading?: boolean,
  isExpanded: boolean,
  isExporting: boolean,
  isAllSortingDisabled?: boolean,
  onSort: (sortOptions: SortOptions) => void,
  onExport: ({ type, ids }: { type: 'selected' | 'all' | 'range', ids?: string[] }) => void,
  onNewExport?: ({ type, ids }: { type: 'selected' | 'all', ids?: string[] }) => Promise<void>,
  /** The checked rows' ids, for the Add to Beacon button. */
  onAddItemsToBeacon?: (ids: string[]) => void,
  onExpandToggle: () => void,
  onEvent?: (event: OnListEventProps) => void,
  showCheckbox?: boolean,
  showExpandButton?: boolean,
  showExportButton?: boolean,
  showAddToBeaconButton?: boolean,
  showRowActionButtons?: boolean,
  showSubheader?: boolean,
  showMainInstitution?: boolean,
  showColumnVisibilityButton?: boolean,
  /** Whether the toolbar and column header stay at the top while the list scrolls. */
  stickyHeader?: boolean,
  currentSort: SortOptions,
  tableFields: TableFields,
  entity: Entity,
  selectedRows?: {[key: string]: any},
  onSelectionChange?: (selectedRows: {[key: string]: any}) => void,
  /** When provided, renders contacts grouped by account with account headers */
  groupedData?: AccountBucket[],
  /** Total contacts across all groups (for display in header) */
  groupedTotalContacts?: number,
  /** Total accounts across all pages (for display in header) */
  groupedTotalAccounts?: number,
  /** Callback to load more contacts for a specific account */
  onLoadMoreContacts?: (accountId: string) => Promise<void>,
  /** Set of account IDs currently loading more contacts */
  loadingMoreAccountIds?: Set<string>,
}, ref: any) => {

  const globalContext = useContext(GlobalContext);
  const newExportEnabled = isNewExportEnabled(globalContext?.authInfo?.teamId);

  const [visibleFields, setVisibleFields] = useState<string[]>(tableFields.getVisibleFields());
  const [isExportMenuOpen, setIsExportMenuOpen] = useState(false);
  const [isNewExportLoading, setIsNewExportLoading] = useState(false);
  const [internalSelectedRows, setInternalSelectedRows] = useState<{[key: string]: any}>({});
  const [selectedScreenshot, setSelectedScreenshot] = useState<UrlVersion|undefined>(undefined);

  // Use external state if provided, otherwise use internal state
  const selectedRows = externalSelectedRows ?? internalSelectedRows;
  const setSelectedRows = onSelectionChange ?? setInternalSelectedRows;
  const selectedRowsCount = Object.keys(selectedRows).length;

  // Flatten contacts from grouped data for selection/export operations
  const flattenedGroupedItems = useMemo(() => {
    if (!groupedData) return null;
    return groupedData.flatMap(account => account.contacts);
  }, [groupedData]);

  // Support both old format (items) and new format (data), and grouped data
  const getItems = () => flattenedGroupedItems ?? searchResponse?.data?.data ?? searchResponse?.data?.items ?? [];
  const getTotal = () => groupedTotalContacts ?? searchResponse?.data?.total ?? 0;
  const getUniqueDistricts = () => searchResponse?.data?.aggregations?.uniqueDistricts;

  useImperativeHandle(ref, () => ({
    resetSelection: () => {
      setSelectedRows({})
    },
  }));

  function handleCheckboxChange(row: any, event: any) {
    const itemId = event.target.id;
    const isChecked = event.target.checked;
    const newState = { ...selectedRows };
    if (isChecked) {
      newState[itemId] = row;
    } else {
      delete newState[itemId];
    }
    setSelectedRows(newState);
  }

  async function toggleAll(event: any) {
    const contactIds: {[key: string]: any} = {};
    for (const item of getItems()) contactIds[item.id] = item;
    if (event.target.checked) {
      // If checked, add all contacts to selectedContacts
      setSelectedRows({
        ...selectedRows,
        ...contactIds
      });
    } else {
      // If unchecked, remove all contacts from selectedContacts
      const updatedContacts = { ...selectedRows };
      for (const key in contactIds) {
        delete updatedContacts[key];
      }
      setSelectedRows(updatedContacts);
    }
  }

  function toggleAccountContacts(account: AccountBucket, event: any) {
    const contactIds: {[key: string]: any} = {};
    for (const contact of account.contacts) contactIds[contact.id] = contact;
    if (event.target.checked) {
      // If checked, add all contacts in this account to selectedRows
      setSelectedRows({
        ...selectedRows,
        ...contactIds
      });
    } else {
      // If unchecked, remove all contacts in this account from selectedRows
      const updatedRows = { ...selectedRows };
      for (const key in contactIds) {
        delete updatedRows[key];
      }
      setSelectedRows(updatedRows);
    }
  }

  function areAllAccountContactsSelected(account: AccountBucket): boolean {
    return account.contacts.length > 0 && account.contacts.every(contact => !!selectedRows[contact.id]);
  }

  return <>
    {
      isLoading && !isPaginationLoading ?
        <LoadingBars numberOfRows={12} /> :
        !getItems().length ?
          <></> :
          <>
            <div className={`custom-list ${groupedData ? 'custom-list--grouped' : 'main-card'} ${stickyHeader ? 'custom-list--sticky-header' : ''}`}>
              {isPaginationLoading && (
                <div className="pagination-overlay">
                  <div className="pagination-loader">
                    <Loader size="large" />
                  </div>
                </div>
              )}
              <div className="custom-list__head">
                <div className='results-row results-header' style={groupedData ? undefined : { borderBottom: 'none' }}>
                  {
                    showCheckbox &&
                      <div className="results-row-left-side" style={{ padding: '5px 0px' }}>
                        <CheckboxField
                          label=""
                          name="subscribe"
                          value="yes"
                          size="large"
                          checked={getItems().length > 0 && getItems().every((item: any) => !!selectedRows[item.id])}
                          onChange={toggleAll}
                        />
                      </div>
                  }
                  <div className="results-row-content">
                    <Flex
                      alignItems='center'
                      justifyContent='space-between'
                    >
                      <Flex gap={0}>
                        <NrMatchesSnippet
                          nrMatches={getTotal()}
                          uniqueDistricts={entity === Entity.CONTACT ? getUniqueDistricts() : undefined}
                          accountCount={groupedTotalAccounts}
                          itemLabel={groupedData ? 'person' : undefined}
                        />
                        <div className='desc-2'>
                          {
                            selectedRowsCount > 0 && <>
                              <span>, { selectedRowsCount } item{ selectedRowsCount === 1 ? '' : 's' } selected</span>
                              <span
                                className="clear-all-link"
                                onClick={() => setSelectedRows({})}
                              >
                                {' '}(clear all)
                              </span>
                            </>
                          }
                        </div>
                      </Flex>
                      <Flex direction='row' gap={5}>
                        {
                          showExpandButton &&
                            <Button
                              size='small'
                              className='tiny'
                              onClick={() => onExpandToggle()}
                            >
                              {
                                isExpanded
                                 ? <BsArrowsCollapseVertical style={{ fontSize: 15 }} />
                                 : <BsArrowsExpandVertical style={{ fontSize: 15 }} />
                              }
                            </Button>
                        }
                        {
                          showColumnVisibilityButton &&
                            <Menu
                              size='small'
                              alignItems='start'
                              trigger={
                                <MenuButton
                                  size='small'
                                  className='tiny'
                                >
                                  <FaRegEye />
                                </MenuButton>
                              }
                            >
                              {
                                tableFields.getOptions().map((fieldOption) => (
                                  <Button
                                    key={fieldOption.fieldName}
                                    isDisabled={!fieldOption.isHideable}
                                    className='menu-item'
                                    onClick={() => {
                                      tableFields.toggleVisibility(fieldOption.fieldName)
                                      setVisibleFields(tableFields.getVisibleFields())
                                    }}
                                  >
                                    <div style={{ width: 22, fontSize: 14, paddingTop: 3 }}>
                                      { tableFields.isFieldVisible(fieldOption.fieldName) ? <FaRegEye /> : <FaRegEyeSlash /> }
                                    </div>
                                      { FieldConfig[fieldOption.fieldName]?.label }
                                  </Button>
                                ))
                              }
                            </Menu>
                        }
                        {
                          showAddToBeaconButton &&
                            <Button
                              size='small'
                              className='tiny'
                              isDisabled={selectedRowsCount === 0 || isExporting}
                              onClick={() => onAddItemsToBeacon?.(Object.keys(selectedRows))}
                            >
                              Add to Beacon ({ selectedRowsCount })
                            </Button>
                        }
                        {
                          showExportButton &&
                            <Menu
                              size='small'
                              alignItems='start'
                              isOpen={isExportMenuOpen}
                              onOpenChange={setIsExportMenuOpen}
                              trigger={
                                <MenuButton
                                  size='small'
                                  className='tiny'
                                  isDisabled={isNewExportLoading}
                                >
                                  {isNewExportLoading ? (
                                    <>
                                      <Loader size='small' color='var(--amplify-colors-purple-50)' style={{ marginRight: 5 }} /> Exporting...
                                    </>
                                  ) : (
                                    <>
                                      Export <BiChevronDown style={{ marginLeft: 5, marginRight: -3, fontSize: 15 }} />
                                    </>
                                  )}
                                </MenuButton>
                              }
                            >
                              {newExportEnabled && onNewExport ? (
                                <>
                                  <Button
                                    key='newexportselected'
                                    disabled={selectedRowsCount === 0 || isExporting || isNewExportLoading}
                                    className='menu-item'
                                    onClick={async () => {
                                      setIsExportMenuOpen(false)
                                      setIsNewExportLoading(true)
                                      try {
                                        await onNewExport({ type: 'selected', ids: Object.keys(selectedRows) })
                                      } finally {
                                        setIsNewExportLoading(false)
                                      }
                                    }}
                                  >
                                    Export Selected ({ selectedRowsCount })
                                  </Button>
                                  <Button
                                    key='newexportall'
                                    disabled={isExporting || isNewExportLoading}
                                    className='menu-item'
                                    onClick={async () => {
                                      setIsExportMenuOpen(false)
                                      setIsNewExportLoading(true)
                                      try {
                                        await onNewExport({ type: 'all' })
                                      } finally {
                                        setIsNewExportLoading(false)
                                      }
                                    }}
                                  >
                                    Export All
                                  </Button>
                                </>
                              ) : (
                                <>
                                  <Button
                                    key='exportselected'
                                    disabled={selectedRowsCount === 0 || isExporting}
                                    className='menu-item'
                                    onClick={() => {
                                      setIsExportMenuOpen(false)
                                      onExport({ type: 'selected', ids: Object.keys(selectedRows) })
                                    }}
                                  >
                                    Export Selected ({ selectedRowsCount })
                                  </Button>
                                  <Button
                                    key='exportall'
                                    disabled={isExporting}
                                    className='menu-item'
                                    onClick={() => {
                                      setIsExportMenuOpen(false)
                                      onExport({ type: 'all' })
                                    }}
                                  >
                                    Export All
                                  </Button>
                                </>
                              )}
                            </Menu>
                        }
                      </Flex>
                    </Flex>
                  </div>
                </div>
                { entity === Entity.CONTACT && (
                  isKeystoneVersion(searchResponse?.data?.version)
                    ? <TableRowHeaderContactKeystone tableFields={tableFields} currentSort={currentSort} onSort={onSort} showSubheader={showSubheader} isAllSortingDisabled={isAllSortingDisabled} />
                    : <TableRowHeaderContact tableFields={tableFields} currentSort={currentSort} onSort={onSort} showSubheader={showSubheader} isAllSortingDisabled={isAllSortingDisabled} />
                )}
                { [Entity.DISTRICT, Entity.SCHOOL, Entity.AGENCY].includes(entity) && <TableRowHeaderInstitution tableFields={tableFields} currentSort={currentSort} onSort={onSort} showSubheader={showSubheader} isAllSortingDisabled={isAllSortingDisabled} /> }
                { entity === Entity.DOMAIN && <TableRowHeader tableFields={tableFields} currentSort={currentSort} onSort={onSort} showSubheader={showSubheader} isAllSortingDisabled={isAllSortingDisabled} /> }
                { entity === Entity.INGESTION && <TableRowHeader tableFields={tableFields} currentSort={currentSort} onSort={onSort} showSubheader={showSubheader} isAllSortingDisabled={isAllSortingDisabled} /> }
                { entity === Entity.RFP && <TableRowHeaderRfp tableFields={tableFields} currentSort={currentSort} onSort={onSort} showSubheader={showSubheader} isAllSortingDisabled={isAllSortingDisabled} /> }
              </div>
              <div className="results-table">
                {groupedData ? (
                  // Grouped rendering mode - contacts grouped by account
                  <div className="grouped-accounts-list">
                    {groupedData.map((account) => {
                      // Find institution matching accountId from first contact's belongsTo
                      // (accountId is district_id preferring district, but belongsTo[0] may be school)
                      const accountInstitution = account.contacts[0]?.belongsTo?.find(
                        (bt: any) => bt.institution?.id === account.accountId
                      )?.institution;
                      const displayName = accountInstitution?.name ?? account.accountName;
                      const institutionType = accountInstitution?.type;
                      const institutionAccess = accountInstitution?.access;

                      return (
                      <div key={account.accountId} className="grouped-account">
                        <div className="grouped-account__header">
                          {showCheckbox && (
                            <div className="grouped-account__checkbox">
                              <CheckboxField
                                label=""
                                name={`account-${account.accountId}`}
                                value="yes"
                                size="large"
                                checked={areAllAccountContactsSelected(account)}
                                onChange={(event) => {
                                  toggleAccountContacts(account, event);
                                }}
                              />
                            </div>
                          )}
                          <span className="grouped-account__name">
                            <Link to={`/directory/institutions/${account.accountId}`}>{displayName}</Link>
                            <span className="grouped-account__count">{account.contactCount} {account.contactCount === 1 ? 'person' : 'people'} found</span>
                          </span>
                          <span className="grouped-account__meta">
                            {institutionType && <UniversalTag category={institutionType} />}
                            {institutionAccess && <UniversalTag category={institutionAccess} />}
                            <PercentileIndicator percentile={account.accountPriority} />
                          </span>
                        </div>
                        <div className="grouped-account__contacts">
                          {account.contacts.map((row: any) => (
                            <div
                              key={row.id}
                              className="results-row results-content clickable"
                              style={{ position: 'relative' }}
                            >
                              {showCheckbox && (
                                <div className="results-row-left-side">
                                  <div style={{ margin: '2px 0' }}>
                                    <CheckboxField
                                      label=""
                                      name="subscribe"
                                      value="yes"
                                      id={row.id}
                                      size="large"
                                      checked={!!selectedRows[row.id]}
                                      onChange={(event) => {
                                        handleCheckboxChange(row, event);
                                        event.stopPropagation();
                                      }}
                                    />
                                  </div>
                                </div>
                              )}
                              <TableRowContactKeystone
                                row={row}
                                tableFields={tableFields}
                                showRowActionButtons={showRowActionButtons}
                                showMainInstitution={showMainInstitution}
                                onTagContact={(data) => {}}
                                onUntagContact={(data) => {}}
                                onEditContact={(data) => {}}
                                onSelectScreenshot={(data) => {
                                  setSelectedScreenshot(data);
                                }}
                              />
                            </div>
                          ))}
                          {account.contactCount > account.contacts.length && onLoadMoreContacts && (
                            <div className="grouped-account__load-more">
                              <Button
                                size="small"
                                className="tiny"
                                isLoading={loadingMoreAccountIds?.has(account.accountId)}
                                loadingText="Loading..."
                                onClick={() => onLoadMoreContacts(account.accountId)}
                              >
                                Load {account.contactCount - account.contacts.length} more
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    );})}
                  </div>
                ) : (
                  // Standard flat rendering mode
                  getItems().map((row: any) => (
                    <div
                      key={row.id}
                      className={`results-row results-content ${entity === Entity.CONTACT ? 'clickable' : ''}`}
                      style={{ position: 'relative' }}
                    >
                      {
                        showCheckbox &&
                          <div className="results-row-left-side">
                            <div style={{ margin: '2px 0' }}>
                              <CheckboxField
                                label=""
                                name="subscribe"
                                value="yes"
                                id={row.id}
                                size="large"
                                checked={!!selectedRows[row.id]}
                                onChange={(event) => {
                                  handleCheckboxChange(row, event)
                                  event.stopPropagation();
                                }}
                              />
                            </div>
                          </div>
                      }
                      {
                        entity === Entity.CONTACT && (
                          isKeystoneVersion(searchResponse?.data?.version)
                            ? <TableRowContactKeystone
                                row={row}
                                tableFields={tableFields}
                                showRowActionButtons={showRowActionButtons}
                                showMainInstitution={showMainInstitution}
                                onTagContact={(data) => {}}
                                onUntagContact={(data) => {}}
                                onEditContact={(data) => {}}
                                onSelectScreenshot={(data) => {
                                  setSelectedScreenshot(data)
                                }}
                              />
                            : <TableRowContact
                                row={row}
                                tableFields={tableFields}
                                showRowActionButtons={showRowActionButtons}
                                showMainInstitution={showMainInstitution}
                                onTagContact={(data) => {}}
                                onUntagContact={(data) => {}}
                                onEditContact={(data) => {}}
                                onSelectScreenshot={(data) => {
                                  setSelectedScreenshot(data)
                                }}
                              />
                        )
                      }
                      { [Entity.DISTRICT, Entity.SCHOOL, Entity.AGENCY].includes(entity) && <TableRowInstitution row={row} tableFields={tableFields} /> }
                      { entity === Entity.DOMAIN && <TableRowDomain row={row} tableFields={tableFields} /> }
                      { entity === Entity.RFP && <TableRowRFP row={row} tableFields={tableFields} /> }
                    </div>
                  ))
                )}
              </div>
            </div>
          </>
    }
    <ReactModal
      isOpen={!!selectedScreenshot}
      shouldCloseOnEsc={true}
      shouldCloseOnOverlayClick={true}
      onRequestClose={() => setSelectedScreenshot(undefined)}
      style={General.getSliderDefaults({ width: 'min(70vw, 1600px)' })}
      closeTimeoutMS={200}
      parentSelector={General.getModalRoot}
    >
      <div className='screenshot-wrapper'>
        {
          selectedScreenshot?.boxes?.map((box: any) => {
            return <>
              {
                box?.boxes?.map((bb: any) => {
                  return <>
                    <div className="bounding-box" style={General.getBoundingBoxPosition(selectedScreenshot.screenshot_size, bb)}></div>
                  </>
                })
              }
            </>
          })
        }
        <Image src={`${CLOUDFRONT_DOMAIN}/url/${selectedScreenshot?.screenshot}`} alt='Screenshot' />
      </div>
    </ReactModal>
  </>
}

export default forwardRef(CustomList);
